import * as THREE from "three";
import gsap from "gsap";

import { domRectToWorld } from "@/scripts/utils/webgl/DOMTracker";
import { createSwapShaderMaterial } from "@/scripts/utils/webgl/SwapShaderMaterial";
import { ShaderPlaneGroup } from "@/scripts/utils/webgl/ShaderPlaneGroup";

interface PreviewSource {
    video: HTMLVideoElement;
    texture: THREE.VideoTexture;
    aspect: number;
    ready: boolean;
}

export class HeaderPreview extends ShaderPlaneGroup {
    protected getMeshes() {
        return this.mesh ? [this.mesh] : [];
    }

    private scene: THREE.Scene;
    private host: HTMLElement | null = null;
    private mesh: THREE.Mesh | null = null;
    private mat: THREE.ShaderMaterial | null = null;
    private sources: Map<string, PreviewSource> = new Map();
    private currentSrc = "";
    private scaleMultiplier = 0.85;
    private isVisible = false;
    private swapTween: gsap.core.Tween | null = null;
    private showRequestId = 0;

    // RGB shift
    private pos = new THREE.Vector2();
    private needsSnap = true;
    private lastTime = 0;
    private stiffness = 9;
    private offsetStrength = 0.55;

    constructor(scene: THREE.Scene) {
        super();
        this.scene = scene;
    }

    init(host: HTMLElement, srcs: string[]): void {
        this.dispose();
        this.host = host;

        const meshAspect = host.offsetWidth / host.offsetHeight || 1;
        const mat = createSwapShaderMaterial(meshAspect);
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 16, 16), mat);
        mesh.frustumCulled = false;
        mesh.renderOrder = 10;
        mesh.visible = false;
        this.scene.add(mesh);

        this.mesh = mesh;
        this.mat = mat;
        this.needsSnap = true;

        for (const src of [...new Set(srcs)]) this.createSource(src);
    }

    private createSource(src: string): PreviewSource {
        const existing = this.sources.get(src);
        if (existing) return existing;

        const video = document.createElement("video");
        video.src = src;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = "auto";
        video.crossOrigin = "anonymous";
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
        video.style.cssText = "position:absolute;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;";
        document.body.appendChild(video);

        const texture = new THREE.VideoTexture(video);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.colorSpace = THREE.SRGBColorSpace;

        const source: PreviewSource = { video, texture, aspect: 1, ready: false };

        video.addEventListener(
            "loadeddata",
            () => {
                source.aspect = video.videoWidth / video.videoHeight;
                source.ready = true;
            },
            { once: true }
        );

        video.load();
        this.sources.set(src, source);
        return source;
    }

    /** direction: +1 hovering downward in the list, -1 upward */
    show(src: string, direction: number): void {
        if (!this.mat || !this.mesh || !src) return;

        const source = this.sources.get(src) ?? this.createSource(src);
        const requestId = ++this.showRequestId;

        const run = () => {
            if (requestId !== this.showRequestId) return;
            if (!this.mat || !this.mesh) return;

            void source.video.play().catch(() => {});

            const first = !this.isVisible || !this.currentSrc;

            if (first) {
                this.mat.uniforms.uTextureA.value = source.texture;
                this.mat.uniforms.uAspectA.value = source.aspect;
                this.mat.uniforms.uTextureB.value = source.texture;
                this.mat.uniforms.uAspectB.value = source.aspect;
                this.mat.uniforms.uProgress.value = 0;
                this.needsSnap = true;
            } else if (src !== this.currentSrc) {
                this.commitSwap();
                this.mat.uniforms.uTextureB.value = source.texture;
                this.mat.uniforms.uAspectB.value = source.aspect;
                this.mat.uniforms.uDirection.value = direction >= 0 ? 1 : -1;
                this.mat.uniforms.uProgress.value = 0;

                this.swapTween = gsap.to(this.mat.uniforms.uProgress, {
                    value: 1,
                    duration: 0.55,
                    ease: "power2.inOut",
                    onComplete: () => this.commitSwap(),
                });

                this.playChannelFlick([this.mat]);
            }

            this.currentSrc = src;
            this.isVisible = true;
            this.mesh.visible = true;

            gsap.to(this.mat.uniforms.uOpacity, { value: 1, duration: 0.35, ease: "expo.out", overwrite: true });
            gsap.to(this, { scaleMultiplier: 1, duration: 0.5, ease: "expo.out", overwrite: "auto" });
        };

        // Gate on a decoded frame — this is what kills the black flash
        if (source.ready) run();
        else source.video.addEventListener("loadeddata", run, { once: true });
    }

    private commitSwap(): void {
        if (!this.mat) return;
        this.swapTween?.kill();
        this.swapTween = null;

        const texB = this.mat.uniforms.uTextureB.value;
        if (texB) {
            this.mat.uniforms.uTextureA.value = texB;
            this.mat.uniforms.uAspectA.value = this.mat.uniforms.uAspectB.value;
        }
        this.mat.uniforms.uProgress.value = 0;

        // Pause everything that isn't on screen
        for (const [src, source] of this.sources) {
            if (src === this.currentSrc) continue;
            source.video.pause();
            source.video.currentTime = 0;
        }
    }

    hide(instant = false): void {
        this.showRequestId++;
        if (!this.mat || !this.mesh) return;

        this.isVisible = false;
        this.currentSrc = "";

        const finish = () => {
            if (this.isVisible) return;
            if (this.mesh) this.mesh.visible = false;
            for (const source of this.sources.values()) {
                source.video.pause();
                source.video.currentTime = 0;
            }
        };

        if (instant) {
            gsap.killTweensOf(this.mat.uniforms.uOpacity);
            gsap.killTweensOf(this);
            this.mat.uniforms.uOpacity.value = 0;
            this.mat.uniforms.uOffset.value.set(0, 0);
            this.scaleMultiplier = 0.85;
            finish();
            return;
        }

        gsap.to(this.mat.uniforms.uOpacity, { value: 0, duration: 0.3, ease: "expo.out", overwrite: true, onComplete: finish });
        gsap.to(this, { scaleMultiplier: 0.85, duration: 0.3, ease: "expo.out", overwrite: "auto" });
    }

    syncAll(camera: THREE.PerspectiveCamera, rendererWidth: number, rendererHeight: number, time: number): void {
        if (!this.mesh || !this.mat || !this.host || !this.mesh.visible) return;

        const dt = this.lastTime ? Math.min(time - this.lastTime, 0.1) : 1 / 60;
        this.lastTime = time;

        const rect = this.host.getBoundingClientRect();
        const { x, y, width, height } = domRectToWorld(rect, camera, rendererWidth, rendererHeight);

        const sw = Math.max(width * this.scaleMultiplier, 1e-4);
        const sh = Math.max(height * this.scaleMultiplier, 1e-4);

        if (this.needsSnap) {
            this.pos.set(x, y);
            this.needsSnap = false;
        } else {
            const k = 1 - Math.exp(-this.stiffness * dt);
            this.pos.x += (x - this.pos.x) * k;
            this.pos.y += (y - this.pos.y) * k;
        }

        // Lag in local plane units, so deformation isn't skewed by aspect
        this.mat.uniforms.uOffset.value.set(((x - this.pos.x) / sw) * this.offsetStrength, ((y - this.pos.y) / sh) * this.offsetStrength);

        this.mesh.position.set(this.pos.x, this.pos.y, 0.02);
        this.mesh.scale.set(sw, sh, 1);

        this.mat.uniforms.uMeshAspect.value = rect.width / rect.height;
        this.mat.uniforms.uTime.value = time;
    }

    dispose(): void {
        this.showRequestId++;
        this.swapTween?.kill();
        gsap.killTweensOf(this);

        for (const source of this.sources.values()) {
            source.video.pause();
            source.video.remove();
            source.texture.dispose();
        }
        this.sources.clear();

        if (this.mesh) {
            this.killUniformTweens(this.mesh.material as THREE.ShaderMaterial);
            this.scene.remove(this.mesh);
            (this.mesh.material as THREE.Material).dispose();
            this.mesh.geometry.dispose();
        }

        this.mesh = null;
        this.mat = null;
        this.host = null;
        this.currentSrc = "";
        this.isVisible = false;
        this.scaleMultiplier = 0.85;
        this.lastTime = 0;
    }
}
